import React from "react";
import { GenerationLog } from "../api/gptApi";

export type GenerationLogRowTestIds = {
  usedData: string;
  usedModel: string;
  usedImages: string;
  usedWebSearch: string;
  usedReasoning: string;
  tokensUsed: string;
  creditsUsed: string;
  detailLink: string;
};

/** What a generation that just ran knows before its event is read back. */
export type FreshGenerationValues = {
  inputText?: string;
  reasoning?: string;
  tokensUsed?: number | null;
  creditsUsed?: string | null;
};

type Props = {
  log?: GenerationLog;
  fresh?: FreshGenerationValues;
  testIds: GenerationLogRowTestIds;
};

export const InfoRow: React.FC<{ label: string; testId?: string }> = ({
  label,
  testId,
  children,
}) => (
  <div data-testid={testId} className="tw-flex tw-flex-col tw-gap-0.5">
    <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-gray-500">
      {label}
    </span>
    <span className="tw-text-sm tw-whitespace-pre-wrap tw-break-words">
      {children}
    </span>
  </div>
);

/** Rendered above everything else in the modal, so it's the first thing seen. */
export const GenerationLogDetailLink: React.FC<{
  log?: GenerationLog;
  testId?: string;
}> = ({ log, testId }) =>
  log?.detail_url ? (
    <a
      href={log.detail_url}
      target="_blank"
      rel="noreferrer"
      data-testid={testId}
      className="tw-text-blue-600 hover:tw-underline"
    >
      View generation event
    </a>
  ) : null;

/**
 * The rows every generation log has, shared by the Copy Assistant and Enrich info
 * modals. Rows with nothing to show are left out rather than shown as empty.
 */
export const GenerationLogRows: React.FC<Props> = ({ log, fresh, testIds }) => {
  const inputText = fresh?.inputText ?? log?.input_text;
  const reasoning = fresh?.reasoning ?? log?.reasoning;
  const tokensUsed = fresh?.tokensUsed ?? log?.tokens_used;
  const creditsUsed = fresh?.creditsUsed ?? log?.credits_used;
  const imageUrls = log?.image_urls ?? [];
  // For openrouter/auto the requested and routed model differ, and both are worth
  // seeing. Enrich only records the routed one, so fall back to whichever we have.
  const model =
    log?.textual_model_name &&
    log.model_name &&
    log.model_name !== log.textual_model_name
      ? `${log.textual_model_name} → ${log.model_name}`
      : log?.textual_model_name ?? log?.model_name;

  return (
    <>
      {model && (
        <InfoRow label="Model" testId={testIds.usedModel}>
          {model}
        </InfoRow>
      )}
      {inputText && (
        <InfoRow label="Data" testId={testIds.usedData}>
          {inputText}
        </InfoRow>
      )}
      {imageUrls.length > 0 && (
        <InfoRow label="Images" testId={testIds.usedImages}>
          <span className="tw-flex tw-flex-col tw-gap-0.5">
            {imageUrls.map((url) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noreferrer"
                className="tw-break-all"
              >
                {url}
              </a>
            ))}
          </span>
        </InfoRow>
      )}
      {log && (
        <InfoRow label="Web search" testId={testIds.usedWebSearch}>
          {log.web_search ? `Yes (${log.web_searches ?? 0} searches)` : "No"}
        </InfoRow>
      )}
      {reasoning && (
        <InfoRow label="Reasoning" testId={testIds.usedReasoning}>
          {reasoning}
        </InfoRow>
      )}
      {tokensUsed != null && (
        <InfoRow label="Tokens used" testId={testIds.tokensUsed}>
          {tokensUsed}
        </InfoRow>
      )}
      {creditsUsed != null && (
        <InfoRow label="Credits used" testId={testIds.creditsUsed}>
          {creditsUsed}
        </InfoRow>
      )}
    </>
  );
};
